/**
 * WegfahrerDashboard.js – Admin-Übersicht aller Fälle
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Fuel, Plus, Filter, ExternalLink, RefreshCw, Search, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Datum immer als TT.MM.JJJJ anzeigen
const fmtDate = (d) => {
  if (!d) return '–';
  // ISO-Format "YYYY-MM-DD" oder "YYYY-MM-DD HH:MM" umwandeln
  const [datePart, timePart] = d.split(' ');
  const parts = datePart.split('-');
  if (parts.length !== 3) return d;
  const german = `${parts[2]}.${parts[1]}.${parts[0]}`;
  return timePart ? `${german} ${timePart}` : german;
};

const STATUS_CONFIG = {
  neu:             { label: 'Neu',                 cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  in_bearbeitung:  { label: 'In Bearbeitung',       cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  an_anwalt:       { label: 'An Anwalt gesendet',   cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  wurde_bezahlt:   { label: 'Wurde bezahlt',         cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
};

export const WegfahrerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', location: '' });
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  // Autocomplete-Vorschläge aus allen Fall-Feldern
  const suggestions = useMemo(() => {
    if (!searchTerm.trim() || searchTerm.length < 2) return [];
    const q = searchTerm.toLowerCase();
    const matches = [];
    for (const c of cases) {
      const fields = [
        { value: c.case_nr,         label: 'Fallnr.',      icon: '#' },
        { value: c.license_plate,   label: 'Kennzeichen',  icon: '🚗' },
        { value: c.incident_date,   label: 'Datum',        icon: '📅' },
        { value: c.fuel_type,       label: 'Kraftstoff',   icon: '⛽' },
        { value: c.pump_id,         label: 'Zapfsäule',    icon: '🔢' },
        { value: c.incident_time,   label: 'Uhrzeit',      icon: '🕐' },
      ];
      for (const f of fields) {
        if (f.value && String(f.value).toLowerCase().includes(q)) {
          matches.push({ caseId: c.id, caseNr: c.case_nr, field: f.label, value: f.value, icon: f.icon, status: c.status });
          break; // je Fall nur 1 Vorschlag
        }
      }
      if (matches.length >= 8) break;
    }
    return matches;
  }, [searchTerm, cases]);

  // Gefilterte Fälle für Tabelle (nach Suchbegriff + Status/Standort)
  const filteredCases = useMemo(() => {
    if (!searchTerm.trim()) return cases;
    const q = searchTerm.toLowerCase();
    return cases.filter(c =>
      [c.case_nr, c.license_plate, c.incident_date, c.fuel_type, c.pump_id, c.incident_time]
        .some(v => v && String(v).toLowerCase().includes(q))
    );
  }, [searchTerm, cases]);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const h = { Authorization: `Bearer ${token}` };
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.location) params.location_id = filter.location;
      const [casesRes, locsRes] = await Promise.all([
        axios.get(`${API}/api/wegfahrer/cases`, { headers: h, params }),
        axios.get(`${API}/api/admin/locations`, { headers: h }),
      ]);
      setCases(casesRes.data || []);
      // Admin sieht alle Standorte seines Kunden
      setLocations(locsRes.data || []);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCases(); }, [filter]);

  // Klick außerhalb schließt Dropdown
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const totals = {
    neu: cases.filter(c => c.status === 'neu').length,
    offen: cases.filter(c => ['neu','in_bearbeitung'].includes(c.status)).length,
    amount: cases.filter(c => c.status !== 'wurde_bezahlt').reduce((s, c) => s + (c.amount || 0), 0),
  };

  return (
    <DashboardLayout>
      <div className="space-y-5 pb-8">
        {/* Header – mobil stapeln */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-xl shrink-0">
              <Fuel className="h-6 w-6 sm:h-7 sm:w-7 text-red-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Wegfahrer-Reporting</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">Kraftstoffdiebstahl dokumentieren</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={fetchCases} className="touch-manipulation">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/wegfahrer/settings')} variant="outline" className="touch-manipulation">
              Einstellungen
            </Button>
            <Button size="sm" onClick={() => navigate('/wegfahrer/neu')} className="bg-red-600 hover:bg-red-700 gap-1.5 touch-manipulation">
              <Plus className="h-4 w-4" /> Neuer Fall
            </Button>
          </div>
        </div>

        {/* KPI Karten – responsive grid */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <Card><CardContent className="p-3 sm:p-4">
            <p className="text-xs text-muted-foreground truncate">Neue Fälle</p>
            <p className="text-xl sm:text-2xl font-bold text-yellow-600">{totals.neu}</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 sm:p-4">
            <p className="text-xs text-muted-foreground truncate">Offen</p>
            <p className="text-xl sm:text-2xl font-bold text-blue-600">{totals.offen}</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 sm:p-4">
            <p className="text-xs text-muted-foreground truncate">Schaden</p>
            <p className="text-xl sm:text-2xl font-bold text-red-600 truncate">{totals.amount.toFixed(0)}€</p>
          </CardContent></Card>
        </div>

        {/* Suche + Filter */}
        <Card>
          <CardContent className="p-3 space-y-2">
            {/* Autocomplete-Suchfeld */}
            <div className="relative" ref={searchRef}>
              <div className="relative flex items-center">
                <Search className="absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
                  placeholder="Suchen nach Fallnr., Kennzeichen, Datum, Kraftstoff..."
                  className="w-full h-9 pl-9 pr-8 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="wegfahrer-search"
                />
                {searchTerm && (
                  <button onClick={() => { setSearchTerm(''); setShowSuggestions(false); }}
                    className="absolute right-2 text-muted-foreground hover:text-foreground">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Dropdown-Vorschläge */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-background border rounded-xl shadow-xl overflow-hidden">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors text-left border-b last:border-0"
                      onMouseDown={() => { navigate(`/admin/wegfahrer/${s.caseId}`); setShowSuggestions(false); }}
                      data-testid={`suggestion-${i}`}
                    >
                      <span className="text-base shrink-0">{s.icon}</span>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-muted-foreground">{s.field}: </span>
                        <span className="text-sm font-medium">
                          {s.field === 'Datum' ? fmtDate(s.value) : s.value}
                        </span>
                      </div>
                      <span className="text-xs font-mono text-muted-foreground shrink-0">{s.caseNr}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full shrink-0 ${STATUS_CONFIG[s.status]?.cls || ''}`}>
                        {STATUS_CONFIG[s.status]?.label || s.status}
                      </span>
                    </button>
                  ))}
                  {filteredCases.length > 0 && (
                    <div className="px-4 py-1.5 bg-muted/30 text-xs text-muted-foreground">
                      {filteredCases.length} Ergebnis{filteredCases.length !== 1 ? 'se' : ''} in Tabelle
                    </div>
                  )}
                </div>
              )}
              {showSuggestions && searchTerm.length >= 2 && suggestions.length === 0 && (
                <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-background border rounded-xl shadow-xl px-4 py-3 text-sm text-muted-foreground">
                  Keine Treffer für „{searchTerm}"
                </div>
              )}
            </div>

            {/* Status + Standort Filter */}
            <div className="flex flex-wrap gap-2 items-center">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select value={filter.status} onChange={e => setFilter(f => ({...f, status: e.target.value}))}
                className="h-8 px-2 rounded-lg border text-xs bg-background">
                <option value="">Alle Status</option>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              {locations.length > 1 && (
                <select value={filter.location} onChange={e => setFilter(f => ({...f, location: e.target.value}))}
                  className="h-8 px-2 rounded-lg border text-xs bg-background">
                  <option value="">Alle Standorte</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
              {(filter.status || filter.location || searchTerm) && (
                <button onClick={() => { setFilter({ status: '', location: '' }); setSearchTerm(''); }}
                  className="text-xs text-muted-foreground hover:text-foreground">Alle Filter löschen</button>
              )}
              {searchTerm && (
                <span className="text-xs text-primary font-medium">
                  {filteredCases.length} von {cases.length} Fällen
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabelle */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-12"><RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : filteredCases.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Search className="h-12 w-12 text-muted-foreground/30" />
                <p className="text-muted-foreground">
                  {searchTerm ? `Keine Fälle für „${searchTerm}"` : 'Keine Fälle vorhanden'}
                </p>
                {!searchTerm && (
                  <Button size="sm" onClick={() => navigate('/wegfahrer/neu')} className="bg-red-600 hover:bg-red-700">
                    <Plus className="h-4 w-4 mr-1" /> Ersten Fall melden
                  </Button>
                )}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-4 py-3 font-semibold text-xs">Fallnr.</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs hidden sm:table-cell">Datum</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs">Status</th>
                    <th className="text-right px-4 py-3 font-semibold text-xs hidden sm:table-cell">Liter</th>
                    <th className="text-right px-4 py-3 font-semibold text-xs">Betrag</th>
                    <th className="text-left px-4 py-3 font-semibold text-xs hidden md:table-cell">Kennzeichen</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCases.map((c, i) => {
                    const sc = STATUS_CONFIG[c.status] || STATUS_CONFIG.neu;
                    return (
                      <tr key={c.id} className={`border-b hover:bg-muted/20 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                        <td className="px-4 py-3 font-mono text-xs font-semibold">{c.case_nr}</td>
                        <td className="px-4 py-3 text-xs hidden sm:table-cell text-muted-foreground">
                          {fmtDate(`${c.incident_date}${c.incident_time ? ' ' + c.incident_time : ''}`)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sc.cls}`}>{sc.label}</span>
                        </td>
                        <td className="px-4 py-3 text-right text-xs hidden sm:table-cell">{c.liters?.toFixed(2)} L</td>
                        <td className="px-4 py-3 text-right text-xs font-semibold text-red-600">{c.amount?.toFixed(2)} €</td>
                        <td className="px-4 py-3 text-xs font-mono hidden md:table-cell">
                          {c.license_plate ? `${c.country_code || 'D'} – ${c.license_plate}` : <span className="text-muted-foreground">–</span>}
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => navigate(`/admin/wegfahrer/${c.id}`)}
                            className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                            <ExternalLink className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default WegfahrerDashboard;
